import { useEffect, useRef, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { cn } from "../lib/utils"

/**
 * Audio waveform visualization — SVG bars with center envelope.
 *
 * Props (resolved via element):
 *   bars: number of bars (default 128)
 *   seed: PRNG seed (default 42)
 *   mode: "scrolling" (animated, bars shift) or "static" (frozen)
 *   active: when true + mode=scrolling, animation plays; when false, static frame
 *   className: wrapper classes
 */
function mulberry32(a: number) {
  return () => {
    a |= 0
    a = (a + 0x6D2B79F5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export function WaveformRenderer({ element }: RendererProps) {
  const { resolveSymbol } = useRenderContext()
  const bars = ((element as any).bars as number) ?? 128
  const seed = ((element as any).seed as number) ?? 42

  // Resolve mode and active from state (support #{...} bindings)
  const rawMode = (element as any).mode
  const modeRaw = typeof rawMode === "string" && rawMode.includes("#{")
    ? String(resolveSymbol(rawMode) ?? "scrolling")
    : (rawMode ?? "scrolling")
  const mode: "scrolling" | "static" = modeRaw === "static" ? "static" : "scrolling"

  const rawActive = (element as any).active
  const activeResolved = typeof rawActive === "string" && rawActive.includes("#{")
    ? resolveSymbol(rawActive)
    : rawActive
  const active = activeResolved === true || activeResolved === "true"

  // Animation: when scrolling+active, shift seed each frame for moving wave illusion
  const [offset, setOffset] = useState(0)
  const rafRef = useRef<number | null>(null)
  useEffect(() => {
    if (mode !== "scrolling" || !active) {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
      return
    }
    let last = performance.now()
    const loop = (now: number) => {
      if (now - last > 60) { // ~16fps for visual movement
        setOffset((o) => (o + 1) % 1000)
        last = now
      }
      rafRef.current = requestAnimationFrame(loop)
    }
    rafRef.current = requestAnimationFrame(loop)
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    }
  }, [mode, active])

  const rng = mulberry32(seed + offset)
  const vbW = 1000
  const vbH = 100
  const slot = vbW / bars
  const barWidth = slot * 0.55

  const envelope = (i: number) => {
    const t = i / (bars - 1)
    const centered = Math.sin(t * Math.PI)
    return Math.pow(centered, 1.4)
  }

  return (
    <svg
      className={cn("block h-full w-full", element.className)}
      viewBox={`0 0 ${vbW} ${vbH}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      {Array.from({ length: bars }, (_, i) => {
        const noise = 0.3 + rng() * 0.7
        const amp = envelope(i) * noise
        const barH = amp * (vbH - 4)
        const x = i * slot + (slot - barWidth) / 2
        const y = (vbH - barH) / 2
        return (
          <rect
            key={i}
            x={x}
            y={y}
            width={barWidth}
            height={barH}
            fill="currentColor"
            rx={barWidth / 2}
          />
        )
      })}
    </svg>
  )
}
